import { Injectable, Injector } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { StpExpiryService } from '../stp-expiry.service';

export const STP_EXPIRED_CODE = 'STP_EXPIRED';
export const STP_STATUS_UNAVAILABLE_CODE = 'STP_STATUS_UNAVAILABLE';

// Messages already shown by the interceptor. Some services turn the
// HttpErrorResponse into its message string, so callbacks may receive either.
const shownStpMessages = new Set<string>();

/**
 * True when the error is a 423 STP_EXPIRED or 503 STP_STATUS_UNAVAILABLE
 * response that StpExpiryInterceptor has already shown to the user.
 * Use it at the top of an error callback to avoid a second message.
 */
export function isStpStatusError(error: any): boolean {
  if (error instanceof HttpErrorResponse) {
    return getStpStatusCode(error) != null;
  }
  if (typeof error === 'string') {
    return shownStpMessages.has(error);
  }
  return false;
}

function getStpStatusCode(error: HttpErrorResponse): string | null {
  const code = error?.error?.code;
  if (error.status === 423 && code === STP_EXPIRED_CODE) {
    return STP_EXPIRED_CODE;
  }
  if (error.status === 503 && code === STP_STATUS_UNAVAILABLE_CODE) {
    return STP_STATUS_UNAVAILABLE_CODE;
  }
  return null;
}

/**
 * Shows one notification for blocked STP write calls and rethrows the error,
 * so callers still run their error callbacks (loading flags etc.).
 * Never logs the user out.
 */
@Injectable()
export class StpExpiryInterceptor implements HttpInterceptor {
  private lastShownMessage = '';
  private lastShownAt = 0;

  // Injector avoids a circular dependency: StpExpiryService uses HttpClient.
  constructor(
    private notification: NzNotificationService,
    private injector: Injector
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const code = error instanceof HttpErrorResponse ? getStpStatusCode(error) : null;
        if (code === STP_EXPIRED_CODE) {
          const message =
            error.error?.message || 'Your STP registration is not active.';
          this.show('error', 'STP Registration Not Active', message);
          this.injector.get(StpExpiryService).refresh();
        } else if (code === STP_STATUS_UNAVAILABLE_CODE) {
          const message =
            error.error?.message ||
            'Could not verify your STP registration. Please try again shortly.';
          this.show('warning', 'Please Try Again', message);
        }
        return throwError(error);
      })
    );
  }

  private show(type: 'error' | 'warning', title: string, message: string): void {
    shownStpMessages.add(message);
    // Parallel calls rejected together show one message.
    const now = Date.now();
    if (message === this.lastShownMessage && now - this.lastShownAt < 3000) {
      return;
    }
    this.lastShownMessage = message;
    this.lastShownAt = now;
    this.notification[type](title, message);
  }
}
